/**
 * Exports a multi-variant comparison to an Excel workbook.
 * Produces: Summary sheet with variants as columns, plus per-variant breakdown sheets.
 *
 * @param {object} params
 * @param {object} params.scenario - The scenario object
 * @param {Array} params.variants - The compared variant objects with costResult
 * @param {string} params.userName - The exporting user's display name
 */
import { getCurrencySymbol, formatMoney, round2 } from "../format-utils"
import { saveWorkbookForExport } from "./save-workbook-for-export"
import { buildScheduleRows } from "./export-scenario-to-excel"

export async function exportComparisonToExcel({ scenario, variants, userName }) {
    const xlsx = await import("xlsx")
    const { utils } = xlsx
    if (!variants?.length) return

    const now = new Date()
    const dateStr = formatIsoDate(now)
    const dateTimeStr = formatDateTime(now)
    const workbook = utils.book_new()

    addSheet(
        workbook,
        utils,
        "Comparison Summary",
        buildComparisonSummaryRows({ scenario, variants, dateTimeStr, userName })
    )

    addSheet(workbook, utils, "Trade Comparison", buildTradeComparisonRows({ variants }))
    addSheet(workbook, utils, "Facility Comparison", buildFacilityComparisonRows({ variants }))
    addVariantScheduleSheets(workbook, utils, variants)

    for (const variant of variants) {
        if (!variant.costResult) continue
        const sheetName = getUniqueSheetName(variant.name, workbook.SheetNames)
        const currencySymbol = getCurrencySymbol(variant.costResult.currency || variant.rateCardCurrency)
        addSheet(workbook, utils, sheetName, buildVariantDetailRows({ variant, currencySymbol }))
    }

    const regimeName = sanitiseFileName(scenario.regimeName || "Regime")
    await saveWorkbookForExport({
        workbook,
        fileName: `Optimiser Comparison -- ${regimeName} -- ${dateStr}.xlsx`,
        xlsx,
    })
}

function addSheet(workbook, utils, sheetName, rows) {
    if (!rows.length) return
    const worksheet = utils.json_to_sheet(rows)
    const keys = Object.keys(rows[0])
    worksheet["!cols"] = keys.map((key) => ({
        wch: rows.reduce((w, r) => Math.max(w, `${r[key] ?? ""}`.length), key.length + 2),
    }))
    utils.book_append_sheet(workbook, worksheet, sheetName)
}

function buildComparisonSummaryRows({ scenario, variants, dateTimeStr, userName }) {
    const rows = []
    const baseline = variants[0]

    rows.push(buildRow("Scenario", scenario.name, variants))
    rows.push(buildRow("Regime", scenario.regimeName || "", variants))
    rows.push(buildRow("Export Date", dateTimeStr, variants))
    rows.push(buildRow("Exported By", userName || "", variants))
    rows.push(buildEmptyRow(variants))

    rows.push(buildRow("Variant Name", "", variants, (v) => v.name))
    rows.push(buildRow("Rate Card", "", variants, (v) => v.rateCardName || ""))
    rows.push(buildRow("Rule Set", "", variants, (v) => v.ruleSetName || "None"))
    rows.push(buildRow("Status", "", variants, (v) => v.status || ""))
    rows.push(buildEmptyRow(variants))

    rows.push(
        buildRow("Annual Cost", "", variants, (v) => {
            const sym = getCurrencySymbol(v.costResult?.currency || v.rateCardCurrency)
            return `${sym}${formatMoney(v.costResult?.grandTotal)}`
        })
    )

    if (baseline?.costResult) {
        rows.push(
            buildRow("Delta vs Baseline", "", variants, (v, i) => {
                if (i === 0) return "Baseline"
                const diff = (v.costResult?.grandTotal || 0) - (baseline.costResult?.grandTotal || 0)
                const sym = getCurrencySymbol(v.costResult?.currency || v.rateCardCurrency)
                const sign = diff >= 0 ? "+" : ""
                return `${sign}${sym}${formatMoney(diff)}`
            })
        )

        rows.push(
            buildRow("% Change", "", variants, (v, i) => {
                if (i === 0) return "Baseline"
                const baseVal = baseline.costResult?.grandTotal || 0
                if (baseVal === 0) return "N/A"
                const diff = (v.costResult?.grandTotal || 0) - baseVal
                const pct = (diff / baseVal) * 100
                const sign = pct >= 0 ? "+" : ""
                return `${sign}${round2(pct)}%`
            })
        )
    }

    rows.push(buildEmptyRow(variants))
    rows.push(buildRow("Total Tasks", "", variants, (v) => v.costResult?.taskCount || 0))
    rows.push(buildRow("Costed Tasks", "", variants, (v) => v.costResult?.costedTaskCount || 0))
    rows.push(buildRow("Trades", "", variants, (v) => v.costResult?.tradeBreakdown?.length || 0))
    rows.push(buildRow("Facilities", "", variants, (v) => v.costResult?.facilityBreakdown?.length || 0))
    rows.push(buildRow("Tasks Excluded", "", variants, (v) => v.costResult?.ruleSetExcludedCount || 0))
    rows.push(buildEmptyRow(variants))
    rows.push(buildRow("Unmapped Skills", "", variants, (v) => v.costResult?.unmappedSkills?.count || 0))
    rows.push(buildRow("Unpriced Trades", "", variants, (v) => v.costResult?.unpricedTrades?.count || 0))

    return rows
}

function buildRow(label, metaValue, variants, valueFn) {
    const row = { Metric: label }
    if (!valueFn) {
        row["Value"] = metaValue
    } else {
        for (let i = 0; i < variants.length; i++) {
            row[variants[i].name || `Variant ${i + 1}`] = valueFn(variants[i], i)
        }
    }
    return row
}

function buildEmptyRow(variants) {
    const row = { Metric: "" }
    for (const v of variants) {
        row[v.name || "Variant"] = ""
    }
    return row
}

function buildVariantDetailRows({ variant, currencySymbol }) {
    const rows = []
    const cr = variant.costResult
    if (!cr) return rows

    rows.push({ Section: "Summary", Field: "Annual Cost", Value: `${currencySymbol}${formatMoney(cr.grandTotal)}` })
    rows.push({ Section: "", Field: "Tasks Costed", Value: `${cr.costedTaskCount || 0} of ${cr.taskCount || 0}` })
    rows.push({ Section: "", Field: "", Value: "" })

    const trades = [...(cr.tradeBreakdown || [])].sort(byTotalCostDesc)
    for (const t of trades) {
        rows.push({
            Section: "Trade",
            Field: t.tradeName,
            Value: `${currencySymbol}${formatMoney(t.totalCost)}`,
            Hours: round2(t.totalHours),
            Rate: `${currencySymbol}${formatMoney(t.hourlyRate)}/hr`,
            "%": `${round2(t.percentage)}%`,
        })
    }
    if (trades.length) rows.push({ Section: "", Field: "", Value: "" })

    const facilities = [...(cr.facilityBreakdown || [])].sort(byTotalCostDesc)
    for (const f of facilities) {
        rows.push({
            Section: "Facility",
            Field: f.facilityName,
            Value: `${currencySymbol}${formatMoney(f.totalCost)}`,
            Hours: round2(f.totalHours),
            "%": `${round2(f.percentage)}%`,
        })
    }

    if (cr.taskBreakdown?.length) {
        if (facilities.length) rows.push({ Section: "", Field: "", Value: "" })
        for (const task of cr.taskBreakdown) {
            rows.push({
                Section: "Task",
                Field: task.title,
                Trade: task.tradeName,
                Facility: task.facilityName,
                Hours: task.annualHours,
                Value: `${currencySymbol}${formatMoney(task.annualCost)}`,
                Multiplier: task.multiplier !== 1 ? `${task.multiplier}x` : "",
            })
        }
    }

    return rows
}

function buildTradeComparisonRows({ variants }) {
    const tradeMap = new Map()
    for (const v of variants) {
        for (const t of v.costResult?.tradeBreakdown || []) {
            if (!tradeMap.has(t.tradeName)) tradeMap.set(t.tradeName, {})
            tradeMap.get(t.tradeName)[v.id] = t.totalCost
        }
    }
    const firstVariant = variants[0]
    const rows = [...tradeMap.entries()]
        .sort((a, b) => (b[1][firstVariant?.id] || 0) - (a[1][firstVariant?.id] || 0))
        .map(([tradeName, costs]) => {
            const row = { Trade: tradeName }
            for (const v of variants) {
                const sym = getCurrencySymbol(v.costResult?.currency || v.rateCardCurrency)
                row[v.name || "Variant"] = costs[v.id] != null ? `${sym}${formatMoney(costs[v.id])}` : "\u2014"
            }
            return row
        })
    return rows
}

function buildFacilityComparisonRows({ variants }) {
    const facilityMap = new Map()
    for (const v of variants) {
        for (const f of v.costResult?.facilityBreakdown || []) {
            if (!facilityMap.has(f.facilityName)) facilityMap.set(f.facilityName, {})
            facilityMap.get(f.facilityName)[v.id] = f.totalCost
        }
    }
    const firstVariant = variants[0]
    const rows = [...facilityMap.entries()]
        .sort((a, b) => (b[1][firstVariant?.id] || 0) - (a[1][firstVariant?.id] || 0))
        .map(([facilityName, costs]) => {
            const row = { Facility: facilityName }
            for (const v of variants) {
                const sym = getCurrencySymbol(v.costResult?.currency || v.rateCardCurrency)
                row[v.name || "Variant"] = costs[v.id] != null ? `${sym}${formatMoney(costs[v.id])}` : "\u2014"
            }
            return row
        })
    return rows
}

function addVariantScheduleSheets(workbook, utils, variants) {
    for (const variant of variants) {
        if (!variant.costResult) continue
        const currencySymbol = getCurrencySymbol(variant.costResult.currency || variant.rateCardCurrency)
        const scheduleRows = buildScheduleRows({ costResult: variant.costResult, currencySymbol })
        const sheetName = getUniqueSheetName(`${variant.name || "Variant"} By Schedule`, workbook.SheetNames)
        addSheet(workbook, utils, sheetName, scheduleRows)
    }
}

function byTotalCostDesc(a, b) {
    return (b.totalCost || 0) - (a.totalCost || 0)
}

function formatIsoDate(date) {
    return date.toISOString().split("T")[0]
}

function formatDateTime(date) {
    return `${formatIsoDate(date)} ${date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`
}

function sanitiseFileName(name) {
    return name.replace(/[<>:"/\\|?*]/g, "_")
}

function sanitiseSheetName(name) {
    return `${name || "Sheet"}`.replace(/[\\/*?[\]:]/g, "_").slice(0, 31)
}

function getUniqueSheetName(name, existingSheetNames) {
    const baseName = sanitiseSheetName(name)
    let sheetName = baseName
    let suffix = 1

    while (existingSheetNames.includes(sheetName)) {
        const suffixText = ` ${suffix}`
        sheetName = `${baseName.slice(0, 31 - suffixText.length)}${suffixText}`
        suffix++
    }

    return sheetName
}
